// src/components/dashboards/charts/DemographicsPanel.tsx
// Age histogram + gender donut side by side.

import React from "react";
import { Box, Skeleton, alpha } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme, { mixToward, seriesGradient } from "./useChartTheme";
import { athenaColors } from "../../../theme/palette";
import { DemographicsData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: DemographicsData | null;
  loading: boolean;
}

const DemographicsPanel: React.FC<Props> = ({ data, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, textPrimary, textSecondary, chartHeight, dark } =
    useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Skeleton variant="rounded" sx={{ flex: "2 1 200px", height: chartHeight, borderRadius: 2 }} />
        <Skeleton variant="rounded" sx={{ flex: "1 1 150px", height: chartHeight, borderRadius: 2 }} />
      </Box>
    );
  }

  const ageOption = {
    title: {
      text: t("dashboard.analytics.age_distribution", "Age Distribution"),
      subtext: t(
        "dashboard.analytics.age_distribution_sub",
        "Number of patients by age group",
      ),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: textSecondary, fontSize: 11 },
      left: 0,
    },
    tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" } },
    // Room at the bottom and the left for the axis names below.
    grid: { ...baseGrid, bottom: "10%", top: "26%" },
    // Named axes: without them a reader has to infer that the bars are
    // patient counts and the buckets are ages. Both are guesses a clinician
    // should not have to make about their own cohort.
    xAxis: {
      ...baseAxis,
      type: "category",
      data: data.age_bins.map((b) => b.label),
      name: t("dashboard.analytics.axis_age_group", "Age group"),
      nameLocation: "middle" as const,
      nameGap: 28,
      nameTextStyle: { color: textSecondary, fontSize: 10 },
    },
    // No y-axis name here. ECharts' containLabel sizes the grid for the tick
    // labels but NOT for an axis name, so a rotated name at any nameGap that
    // clears "5,000" starts eating the chart instead. The subtitle above
    // already says these are patient counts.
    yAxis: { ...baseAxis, type: "value" },
    series: [
      {
        type: "bar",
        data: data.age_bins.map((b) => b.count),
        itemStyle: {
          color: seriesGradient(
            seriesColors[0],
            "vertical",
            dark ? athenaColors.royalBlue : undefined,
          ),
          borderRadius: [4, 4, 0, 0],
        },
        barMaxWidth: 32,
      },
    ],
  };

  // Purple, blue, amber: three brand hues that stay apart on navy. Female
  // leads with the purple because that is the slice the gradient was asked for.
  const GENDER_COLORS = [
    athenaColors.lightPurple,
    mixToward(athenaColors.royalBlue, athenaColors.periwinkle, 0.6),
    athenaColors.lightOrange,
  ];

  // Counts alone do not answer "what is the split?" without mental arithmetic;
  // the legend carries the share, which is the question a donut is asked.
  const genderTotal = data.gender.reduce((sum, g) => sum + g.count, 0);
  const genderPct = new Map(
    data.gender.map((g) => [
      g.label.charAt(0).toUpperCase() + g.label.slice(1),
      genderTotal > 0 ? Math.round((g.count / genderTotal) * 100) : 0,
    ]),
  );

  const genderOption = {
    title: {
      text: t("dashboard.analytics.gender_split", "Gender"),
      subtext: t(
        "dashboard.analytics.gender_split_sub",
        "Patient distribution by gender",
      ),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      subtextStyle: { color: textSecondary, fontSize: 11 },
      left: "center",
    },
    tooltip: { ...baseTooltip, trigger: "item" },
    legend: {
      bottom: 0,
      left: "center",
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 12,
      textStyle: { color: textSecondary, fontSize: 10 },
      formatter: (name: string) => `${name}  ${genderPct.get(name) ?? 0}%`,
    },
    series: [
      {
        type: "pie",
        radius: ["45%", "70%"],
        center: ["50%", "52%"],
        data: data.gender.map((g, i) => ({
          name: g.label.charAt(0).toUpperCase() + g.label.slice(1),
          value: g.count,
          itemStyle: {
            // Joan, 2026-09-10: the ask was for the PURPLE to become a
            // gradient purple -> royalBlue, not for the purple to be replaced.
            // Swapping it for periwinkle was the wrong reading. So: an
            // explicit brand trio, purple first, and each slice ramps toward
            // royalBlue rather than toward transparency (a plain alpha fade
            // left the bottom of a slice see-through, which on a ring reads as
            // a missing segment).
            color: dark
              ? seriesGradient(
                  GENDER_COLORS[i % GENDER_COLORS.length],
                  "vertical",
                  athenaColors.royalBlue,
                  0.6,
                )
              : seriesColors[i % seriesColors.length],
            // Present, not showy: 18/0.45 put a visible halo around the whole
            // ring, which on a page whose only other glow is a 16px bloom on
            // four icon tiles made the donut the loudest thing on it.
            ...(dark && {
              shadowBlur: 10,
              shadowColor: alpha(GENDER_COLORS[i % GENDER_COLORS.length], 0.22),
            }),
          },
        })),
        // The legend below carries every name and its share, so the outside
        // labels are a duplicate that collides with the subtitle above.
        label: { show: false },
        emphasis: { label: { show: false } },
      },
    ],
  };

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ flex: "2 1 220px", height: chartHeight }}>
        <ReactECharts option={ageOption} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
      </Box>
      <Box sx={{ flex: "1 1 160px", height: chartHeight }}>
        <ReactECharts option={genderOption} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
      </Box>
    </Box>
  );
};

export default DemographicsPanel;
